import { delay, fork, put, select, takeEvery } from "redux-saga/effects";

import Note from "@mapmycustomers/shared/types/entity/common/Note";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import MmcNotificationCategory from "@app/enum/MmcNotificationCategory";
import { getScope } from "@app/scene/dashboard/store/selectors";
import { logout } from "@app/store/auth/actions";
import authService from "@app/store/auth/AuthService";
import { fetchNotifications } from "@app/store/notification/actions";
import Scope from "@app/types/dashboard/Scope";
import MmcNotification from "@app/types/MmcNotification";

import { bumpNotesRefreshCounter, fetchReplies } from "./notes/actions";
import { bumpTasksRefreshCounter } from "./tasks/actions";

const POLL_INTERVAL_MS = 60_000;

// Each poll returns the full unread batch — including events we already acted on.
const PROCESSED_ID_CAP = 200;
const processedNotificationIds = new Set<MmcNotification["id"]>();

function markProcessed(id: MmcNotification["id"]) {
  if (processedNotificationIds.size >= PROCESSED_ID_CAP) {
    // Drop the oldest entry (insertion-ordered Set).
    const oldest = processedNotificationIds.values().next().value;
    if (oldest !== undefined) {
      processedNotificationIds.delete(oldest);
    }
  }
  processedNotificationIds.add(id);
}

export function* onFetchNotificationsSuccess({
  payload,
}: ReturnType<typeof fetchNotifications.success>) {
  const scope: Scope | undefined = yield select(getScope);
  const scopeUserId = scope?.userId;
  if (!scopeUserId) {
    return;
  }

  let bumpNotes = false;
  let bumpTasks = false;
  const parentNoteIdsToRefetch = new Set<Note["id"]>();

  for (const notification of payload.notifications) {
    if (processedNotificationIds.has(notification.id)) {
      continue;
    }
    if (notification.subjectUserId !== scopeUserId) {
      continue;
    }

    if (notification.category === MmcNotificationCategory.COACHING_NOTE) {
      if (isNotEmpty(notification.parentNoteId)) {
        parentNoteIdsToRefetch.add(notification.parentNoteId);
      } else {
        bumpNotes = true;
      }
      markProcessed(notification.id);
    } else if (notification.category === MmcNotificationCategory.COACHING_TASK) {
      bumpTasks = true;
      markProcessed(notification.id);
    }
  }

  if (bumpNotes) {
    yield put(bumpNotesRefreshCounter());
  }
  for (const parentNoteId of parentNoteIdsToRefetch) {
    yield put(fetchReplies.request({ parentNoteId }));
  }
  if (bumpTasks) {
    yield put(bumpTasksRefreshCounter());
  }
}

// Drives the full-batch notification fetch only while a coaching scope is active.
export function* pollCoachingScopedNotifications() {
  while (true) {
    // Delay first so the initial poll fires one interval after mount instead
    yield delay(POLL_INTERVAL_MS);
    // Skip while the tab is hidden — nobody's watching the feed.
    if (typeof document !== "undefined" && document.hidden) {
      continue;
    }
    if (authService.hasToken()) {
      const scope: Scope | undefined = yield select(getScope);
      if (scope?.userId) {
        yield put(fetchNotifications.request({ silent: true }));
        // Refresh the feeds directly instead of relying on notifications: a
        // user adding their own note/task never notifies the scoped owner, so
        // a notification-driven refetch would never fire for them. The panels
        // refetch silently on counter bump (merging fresh page-1, no scroll
        // reset). onFetchNotificationsSuccess still refetches open reply
        // threads off the notification batch.
        yield put(bumpNotesRefreshCounter());
        yield put(bumpTasksRefreshCounter());
      }
    }
  }
}

export function __resetProcessedNotificationIdsForTest() {
  processedNotificationIds.clear();
}

function onLogout() {
  processedNotificationIds.clear();
}

export function* coachingNotificationsPollingSagas() {
  yield takeEvery(fetchNotifications.success, onFetchNotificationsSuccess);
  yield takeEvery(logout, onLogout);
  yield fork(pollCoachingScopedNotifications);
}
